import React, { useState, useEffect } from "react";
import { Table, Spin, Alert, Collapse, Button, Tooltip, Input, Select, message, Modal } from "antd";
import api from "../functions/api";
import epochToDate from "../functions/epochdate";
import { testAlert, getAlertEffectivenessData, renderAlertEffectiveness } from "../functions/alertClauseService";
import { fetchScopeAffirmations } from "../functions/alertScopeService";
import AlertTestingResultsTable from "./AlertTestingResultsTable";

const { Panel } = Collapse;

const AlertTable = ({
  alerts,
  alertsLoading,
  alertsError,
  tableTitle,
  onEditAlert,
  onDeleteAlert,
  alertEffectiveness,
  onAffirmationClauses,
  onClauseTweaks,
  onClauseProposals,
  onCopyToProd, // DAS-2273: open Copy-to-PROD preview modal
}) => {
  const [searchText, setSearchText] = useState(""); // Holds the search text
  const [filteredAlerts, setFilteredAlerts] = useState([]); // Holds the sorted and filtered data
  const [alertTestCount, setAlertTestCount] = useState(0); // Number of alerts from the test
  const [alertTestResults, setAlertTestResults] = useState([]); // Store results for Alert Test
  const [isResultsModalVisible, setIsResultsModalVisible] = useState(false); // Controls modal visibility
  const [scopeAffirmations, setScopeAffirmations] = useState([]); // Store scope/alert-type affirmations
  // DAS-2374: Backtest Origin modal for AI-Discovered alerts
  const [originModalAlertId, setOriginModalAlertId] = useState(null);
  const [originData, setOriginData] = useState(null);
  const [originLoading, setOriginLoading] = useState(false);

  const openBacktestOrigin = async (alertId) => {
    setOriginModalAlertId(alertId);
    setOriginLoading(true);
    setOriginData(null);
    try {
      const res = await api.get(`/alert_discovery/origin_for_alert/${alertId}`);
      setOriginData(res.data);
    } catch (e) {
      message.error("Failed to load backtest origin");
    } finally {
      setOriginLoading(false);
    }
  };

  // An alert came from the AI Discovery pipeline if its subtype ends with
  // "(AID)" (post-DAS-2367 convention) or is the legacy literal "AI-Discovered".
  const isAiDiscovered = (record) => {
    const s = (record?.alert_subtype || "").trim();
    return s === "AI-Discovered" || /\(AID\)\s*$/i.test(s);
  };

  const fmtPct = (v) => (v == null || v === "" ? "—" : `${Number(v).toFixed(2)}%`);
  const fmtNum = (v) => (v == null || v === "" ? "—" : Number(v).toFixed(2));

  // console.log("alerts in AlertTable ", alerts);
  // console.log("alertEffectiveness in AlertTable ", alertEffectiveness);

  useEffect(() => {
    if (Array.isArray(alerts) && alerts.length > 0) {
      const sortedAlerts = alerts.slice().sort((a, b) => b.epoch_last_edit - a.epoch_last_edit);
      setFilteredAlerts(sortedAlerts);
    } else {
      setFilteredAlerts([]); // Clear filtered alerts if no data
    }
  }, [alerts]);

  // Fetch scope/alert-type affirmations
  useEffect(() => {
    const loadScopeAffirmations = async () => {
      try {
        const affirmations = await fetchScopeAffirmations();
        setScopeAffirmations(affirmations);
      } catch (error) {
        console.error('Error loading scope affirmations:', error);
      }
    };
    loadScopeAffirmations();
  }, []);

  useEffect(() => {
    filteredAlerts.forEach(alert => {
      const matchingEffectiveness = alertEffectiveness.find(effectiveness => effectiveness.alert_subtype_identifier === alert.ID.toString());
      if (matchingEffectiveness) {
        // console.log(`Matching Effectiveness for Alert ID ${alert.ID}:`);
        // console.log(`ID: ${matchingEffectiveness.ID}`);
        // console.log(`n_total: ${matchingEffectiveness.n_total}`);
        // console.log(`alert_subtype: ${matchingEffectiveness.alert_subtype}`);
        
        // Log all fields with 'AVG' in them
        // Object.keys(matchingEffectiveness).forEach(key => {
        //   if (key.includes('AVG')) {
        //     console.log(`${key}: ${matchingEffectiveness[key]}`);
        //   }
        // });
      }
    });
  }, [filteredAlerts, alertEffectiveness]);

  // Function to handle Alert Test
  const handleTestAlert = async (alertID) => {
    try {
      const result = await testAlert(alertID);
      setAlertTestCount(result.length);
      setAlertTestResults(result);
      setIsResultsModalVisible(true);
      message.success(`Alert ${alertID} tested successfully!`);
    } catch (error) {
      console.error("Error testing alert:", error);
      message.error(`Error testing alert ${alertID}.`);
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
    const filteredData = alerts.filter((alert) =>
      alert.ID.toString().includes(value) ||
      alert.scope.toLowerCase().includes(value.toLowerCase()) ||
      alert.alert_type.toLowerCase().includes(value.toLowerCase()) ||
      alert.advice_txt.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredAlerts(filteredData);
  };

  const formatDate = (epoch) => {
    if (!epoch) return '—';
    try {
      const fullDate = epochToDate(Number(epoch));
      return fullDate.split(" ")[0];
    } catch {
      return '—';
    }
  };

  const alertColumns = [
    { title: "ID", dataIndex: "ID", key: "ID", sorter: (a, b) => a.ID - b.ID },
    { title: "Scope", dataIndex: "scope", key: "scope", sorter: (a, b) => a.scope.localeCompare(b.scope) },
    { title: "Alert Type", dataIndex: "alert_type", key: "alert_type", sorter: (a, b) => a.alert_type.localeCompare(b.alert_type) },
    {
      title: "Advice Text",
      dataIndex: "advice_txt",
      key: "advice_txt",
      sorter: (a, b) => a.advice_txt.localeCompare(b.advice_txt),
      render: (text) => text,
    },
    { 
      title: "Clauses", 
      key: "clauses",
      render: (text, record) => {
        const knockoutClauses = record.clauses || '';
        const affirmativeClauses = record.affirmative_clauses || '';
        
        // Find scope/alert-type affirmative clauses
        const scopeAlertTypeAffirmations = scopeAffirmations.find(
          affirmation => affirmation.Scope === record.scope && affirmation.alert_type === record.alert_type
        );
        const scopeAlertTypeClauses = scopeAlertTypeAffirmations?.affirmative_clauses || '';
        
        return (
          <div>
            {knockoutClauses && (
              <div>
                <strong>KO:</strong> {knockoutClauses}
              </div>
            )}
            {affirmativeClauses && (
              <div>
                <strong>AFF:</strong> {affirmativeClauses}
              </div>
            )}
            {scopeAlertTypeClauses && (
              <div>
                <strong>Scope-Alert type AFF:</strong> {scopeAlertTypeClauses}
              </div>
            )}
            {!knockoutClauses && !affirmativeClauses && !scopeAlertTypeClauses && (
              <span style={{ color: '#999' }}>No clauses</span>
            )}
          </div>
        );
      }
    },
    {
      title: "Subtype",
      dataIndex: "alert_subtype",
      key: "alert_subtype",
      sorter: (a, b) => a.alert_subtype.localeCompare(b.alert_subtype),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span style={{ color: status === 1 ? "green" : "red", fontWeight: "bold" }}>
          {status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "n_total",
      dataIndex: "ID",
      key: "n_total",
      sorter: (a, b) => {
        const effectivenessA = alertEffectiveness.find(effectiveness => effectiveness.alert_subtype_identifier === a.ID.toString());
        const effectivenessB = alertEffectiveness.find(effectiveness => effectiveness.alert_subtype_identifier === b.ID.toString());
        return (effectivenessA?.n_total || 0) - (effectivenessB?.n_total || 0);
      },
      render: (id) => {
        const effectiveness = alertEffectiveness.find(effectiveness => effectiveness.alert_subtype_identifier === id.toString());
        return effectiveness ? effectiveness.n_total : 'N/A';
      },
    },
    {
      title: "Alert Effectiveness",
      key: "alert_effectiveness",
      width: 185,
      render: (text, record) => renderAlertEffectiveness(alertEffectiveness, record.ID.toString()),
    },
    {
      title: "Last Edited",
      dataIndex: "epoch_last_edit",
      key: "epoch_last_edit",
      render: (epoch) => formatDate(epoch),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <>
          <Button type="link" onClick={() => onEditAlert(record.ID)}>
            Edit
          </Button>
          <Button type="link" danger onClick={() => onDeleteAlert(record.ID)}>
            Delete
          </Button>
          <Button type="link" onClick={() => handleTestAlert(record.ID)}>
            Test
          </Button>
          {onAffirmationClauses && (
            <Button type="link" onClick={() => onAffirmationClauses(record.ID)}>
              Affirmation clauses - add existing
            </Button>
          )}
          {onClauseTweaks && (
            <Button type="link" onClick={() => onClauseTweaks(record.ID)}>
              Affirmation Clauses - Tweak
            </Button>
          )}
          {onClauseProposals && (
            <Button type="link" onClick={() => onClauseProposals(record.ID)}>
              Clause Proposals
            </Button>
          )}
          {onCopyToProd && (
            <Button type="link" onClick={() => onCopyToProd(record.ID)}>
              Copy to PROD
            </Button>
          )}
          {isAiDiscovered(record) && (
            <Button
              type="link"
              onClick={() => openBacktestOrigin(record.ID)}
              title="View the AI Discovery backtest that produced this alert"
            >
              Backtest origin
            </Button>
          )}
        </>
      ),
    },
  ];

  return (
    <Collapse defaultActiveKey={["1"]} size="small">
      <Panel header={tableTitle} key="1">
        <div style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="Search alerts"
            onSearch={handleSearch}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: "100%", marginBottom: "16px" }}
            allowClear
          />
        </div>
        {alertsLoading ? (
          <Spin tip="Loading alerts..." />
        ) : alertsError ? (
          <Alert message={alertsError} type="error" />
        ) : (
          <Table
            columns={alertColumns}
            dataSource={filteredAlerts} // Use the pre-sorted data
            pagination={{ pageSize: 5 }}
            rowKey="ID"
            bordered
            size="small"
          />
        )}
        <AlertTestingResultsTable
          visible={isResultsModalVisible}
          onClose={() => setIsResultsModalVisible(false)}
          testResults={alertTestResults}
          columnNames={["Alert text", "Ticker", "User"]}
        />
        <Modal
          title={`Backtest Origin — Alert #${originModalAlertId ?? ''}`}
          open={originModalAlertId !== null}
          onCancel={() => { setOriginModalAlertId(null); setOriginData(null); }}
          footer={null}
          width={760}
        >
          {originLoading && <Spin />}
          {!originLoading && originData && !originData.is_ai_discovered && (
            <Alert
              type="info"
              showIcon
              message="Not an AI-Discovered alert"
              description="No backtest origin recorded for this alert. It was authored manually."
            />
          )}
          {!originLoading && originData?.is_ai_discovered && originData.origin && (
            <div style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
              <p>
                <strong>Discovery run:</strong> #{originData.origin.run_id}{' '}
                · <strong>Candidate:</strong> #{originData.origin.candidate_id}{' '}
                · <strong>Created:</strong> {originData.origin.created_at || '—'}
              </p>
              <p>
                <strong>Type:</strong> {originData.origin.alert_type}{' '}
                · <strong>Scope:</strong> {originData.origin.scope}{' '}
                · <strong>Subtype:</strong> {originData.origin.alert_subtype}{' '}
                · <strong>Refined:</strong>{' '}
                {originData.origin.is_refined
                  ? `yes (${originData.origin.sample_frequency_days ?? '?'}d sampling)`
                  : 'no'}
              </p>
              <p><strong>Clauses used:</strong> {(originData.origin.clauses_json || []).join(', ')}</p>
              <p>
                <strong>Rationale:</strong>{' '}
                <em>{originData.origin.rationale || '—'}</em>
              </p>
              <h4 style={{ marginTop: 16, marginBottom: 4 }}>Backtest metrics</h4>
              <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                    <th style={{ padding: 4 }}>Metric</th>
                    <th style={{ padding: 4 }}>1m</th>
                    <th style={{ padding: 4 }}>6m</th>
                    <th style={{ padding: 4 }}>1y</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: 4 }}>Hit rate (direction)</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.hit_rate_1m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.hit_rate_6m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.hit_rate_1y)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 4 }}>Avg return</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.avg_return_1m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.avg_return_6m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.avg_return_1y)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 4 }}>Avg α vs S&P 500</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.avg_alpha_sp500_1m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.avg_alpha_sp500_6m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.avg_alpha_sp500_1y)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 4 }}>Avg α vs MSCI World</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.avg_alpha_msci_1m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.avg_alpha_msci_6m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.avg_alpha_msci_1y)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 4 }}>% fires beating S&P 500</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.hit_rate_alpha_sp500_1m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.hit_rate_alpha_sp500_6m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.hit_rate_alpha_sp500_1y)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 4 }}>% fires beating MSCI</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.hit_rate_alpha_msci_1m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.hit_rate_alpha_msci_6m)}</td>
                    <td style={{ padding: 4 }}>{fmtPct(originData.origin.hit_rate_alpha_msci_1y)}</td>
                  </tr>
                </tbody>
              </table>
              <p style={{ marginTop: 12 }}>
                <strong>Total fires:</strong> {fmtNum(originData.origin.total_fires)}{' '}
                · <strong>Fires/yr:</strong> {fmtNum(originData.origin.fires_per_year)}{' '}
                · <strong>Composite score:</strong> {fmtNum(originData.origin.composite_score)}
              </p>
              <p style={{ fontSize: '0.75rem', color: '#888', marginTop: 12 }}>
                Per-fire detail events:{' '}
                {originData.origin.events_remaining > 0
                  ? `${originData.origin.events_remaining} retained`
                  : 'purged (older than 90 days)'}
                {' '}— see DAS-2374 lifecycle policy.
              </p>
            </div>
          )}
        </Modal>
      </Panel>
    </Collapse>
  );
};

export default AlertTable;
